/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect, useState } from 'react';
import { InformationPanel } from '@gerdau/hefesto-mais-web/InformationPanel';
import { Variants } from '@gerdau/hefesto/_shared/types';
import { Button } from '@gerdau/hefesto/Button';
import { Icon } from '@gerdau/hefesto/Icon';
import { useDispatch, useSelector } from 'react-redux';
import {
  downloadInvoiceBillet,
  downloadInvoiceXml,
  downloadNFFileFromFinancials,
  loadInvoiceBillet,
  loadInvoiceXml,
  loadNFFileFromFinancials
} from '../../../../../../store/ducks/invoices/actions';
import {
  EInvoiceStatus,
  IInvoiceData
} from '../../../../../../store/ducks/invoices/types';
import { IDownloadNFEResponse } from '../../../../../../store/ducks/long-steel-orders/types';
import { ApplicationState } from '../../../../../../store';
import { sendTagging } from '../../../../../../store/ducks/tagging/actions';
import { ITaggingParams } from '../../../../../../store/ducks/tagging/types';
import { SpinnerIcon } from '../../../../../../components/Commons/SpinnerIcon/styles';
import TicketUnavailableModal from '../../../../../../components/TicketUnavailableModal';
import { downloadPdf } from '../../../../../../utils/createDownloadFile';
import {
  RcaKind,
  sendRcaLogs
} from '../../../../../../store/ducks/observability/actions';

interface IDocumentsColumnProps {
  invoice: IInvoiceData;
}

interface IDocumentsOptions {
  text: string;
  handleOnClick: () => void;
  icon?: JSX.Element;
  variant?: Extract<Variants, 'danger' | 'primary'>;
  hideOption?: boolean;
  disabled?: boolean;
}

const DocumentsColumn: React.FC<IDocumentsColumnProps> = ({ invoice }) => {
  const dispatch = useDispatch();
  const customerId = useSelector(
    (state: ApplicationState) =>
      state.invoices.initialInputValues?.selectedCustomerId || ''
  );
  const customerViewType = useSelector(
    (state: ApplicationState) => state.user.searchCustomerType
  );
  const prepareAndSendTagging = (itemName: string, itemDescription: string) => {
    const taggingParams: ITaggingParams = {
      screenName: 'LG:Financas:MinhasFinancas:Duplicatas',
      event: 'click',
      itemName: itemName,
      itemDescription: itemDescription
    };
    dispatch(sendTagging(taggingParams));
  };

  const [isModalOpen, setIsModalOpen] = useState(false);

  // NF
  const handleDownloadNF = () => {
    prepareAndSendTagging(
      'button:baixar_nota_fiscal',
      'financas_minhas_financas_duplicatas_baixarNotaFiscal'
    );

    if (invoice.nfState?.downloadNFEResponse) {
      downloadFile(invoice.nfState.downloadNFEResponse);
    } else {
      dispatch(loadNFFileFromFinancials(invoice, customerId, customerViewType));
      dispatch(
        sendRcaLogs(RcaKind.DownloadItem, 'NF-F', invoice.invoiceNumber)
      );
    }
  };

  const downloadNFFromUseEffect = () => {
    dispatch(downloadNFFileFromFinancials(invoice));
    downloadFile(invoice?.nfState?.downloadNFEResponse!);
  };

  useEffect(() => {
    if (
      invoice.nfState &&
      !invoice.nfState.alreadyDownloaded &&
      invoice.nfState.downloadNFEResponse
    ) {
      downloadNFFromUseEffect();
    }

    if (
      !invoice.xmlState?.alreadyDownloaded &&
      invoice.xmlState?.invoiceXmlResponse
    ) {
      downloadXMLFile(
        invoice.xmlState.invoiceXmlResponse.FILE,
        `NF-${invoice.invoiceNumber}.xml`
      );
    }
  });

  const downloadFile = (downloadNFEResponse: IDownloadNFEResponse) => {
    const dataurl = 'data:text/plain;base64,' + downloadNFEResponse.FILE;
    const element = document.createElement('a');
    element.setAttribute('href', dataurl);
    element.setAttribute('download', `NF-${invoice.invoiceNumber}.pdf`);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  // BILLET
  const handleDownloadBillet = () => {
    prepareAndSendTagging(
      'button:segunda_via_boleto',
      'financas_minhas_financas_duplicatas_segundaViaBoleto'
    );

    const invoiceType = invoice.invoiceType?.toUpperCase();

    if (invoiceType === 'D' || invoiceType === 'M') {
      setIsModalOpen(true);
    } else {
      if (
        invoice.billetState?.invoiceBilletResponse &&
        invoice.billetState?.invoiceBilletResponse.pdfContent
      ) {
        downloadPdf(invoice.billetState.invoiceBilletResponse.pdfContent);
      } else {
        dispatch(loadInvoiceBillet(invoice));
        dispatch(
          sendRcaLogs(RcaKind.DownloadItem, 'DBIL', invoice.invoiceNumber)
        );
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleDownloadXML = () => {
    prepareAndSendTagging(
      'button:baixar_xml',
      'financas_minhas_financas_duplicatas_baixarXml'
    );

    if (invoice.xmlState && invoice.xmlState.invoiceXmlResponse) {
      downloadXMLFile(
        invoice.xmlState.invoiceXmlResponse.FILE,
        `NF-${invoice.invoiceNumber}.xml`
      );
    } else {
      dispatch(loadInvoiceXml(invoice));
      dispatch(
        sendRcaLogs(RcaKind.DownloadItem, 'INVXML', invoice.invoiceNumber)
      );
    }
  };

  const downloadInvoiceBilletFromUseEffect = () => {
    dispatch(downloadInvoiceBillet(invoice));
    downloadPdf(invoice.billetState.invoiceBilletResponse!.pdfContent);
  };

  useEffect(() => {
    if (
      invoice.billetState &&
      !invoice.billetState.alreadyDownloaded &&
      invoice.billetState.invoiceBilletResponse
    ) {
      downloadInvoiceBilletFromUseEffect();
    }
  });

  const downloadXMLFile = (downloadNFEResponse: string, fileName: string) => {
    dispatch(downloadInvoiceXml(invoice));
    dispatch(
      sendRcaLogs(RcaKind.DownloadItem, 'INVXML', invoice.invoiceNumber)
    );

    const byteCharacters = atob(downloadNFEResponse);
    const byteNumbers = byteCharacters
      .split('')
      .map((_, idx) => byteCharacters.charCodeAt(idx));
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/xml' });

    const link = document.createElement('a');
    link.download = fileName;
    link.href = window.URL.createObjectURL(blob);
    link.click();
  };

  const optionsDocumentsXML: IDocumentsOptions[] = [
    {
      text: invoice?.xmlState?.loading ? 'Baixando XML' : 'Baixar XML',
      handleOnClick: handleDownloadXML,
      variant: 'primary',
      disabled: Boolean(invoice?.xmlState?.loading),
      icon: invoice?.xmlState?.loading ? (
        <SpinnerIcon style={{ marginRight: '0.125rem' }} />
      ) : (
        <Icon option="Download" />
      )
    },
    {
      text: invoice?.nfState?.loading ? 'Baixando NF' : 'Baixar nota fiscal',
      handleOnClick: handleDownloadNF,
      variant: 'primary',
      disabled: Boolean(invoice?.nfState?.loading),
      icon: invoice?.nfState?.loading ? (
        <SpinnerIcon style={{ marginRight: '0.125rem' }} />
      ) : (
        <Icon option="Download" />
      )
    },
    {
      text: invoice?.billetState?.loading
        ? 'Baixando 2ª via'
        : '2ª via de boleto',
      handleOnClick: handleDownloadBillet,
      variant: 'primary',
      disabled:
        Boolean(invoice?.billetState?.loading) ||
        invoice.bankCode === '' ||
        invoice.bankSlipNumber === '',
      icon: invoice?.billetState?.loading ? (
        <SpinnerIcon style={{ marginRight: '0.125rem' }} />
      ) : (
        <Icon option="Download" />
      ),
      hideOption: invoice.status === EInvoiceStatus.CLOSED
    }
  ];

  return (
    <>
      <InformationPanel
        trigger={
          <div>
            <Button
              name="documents"
              variant="tertiary"
              icon={<Icon option="MoreVertical"></Icon>}
            >
              Documentos
            </Button>
          </div>
        }
        position="bottom right"
        title="Documentos"
        panelOptions={optionsDocumentsXML.filter((opt) => !opt.hideOption)}
      />
      {isModalOpen && (
        <TicketUnavailableModal
          message="Verifique as condições de pagamento do seu pedido"
          title="Boleto indisponível"
          onClose={handleCloseModal}
        />
      )}
    </>
  );
};

export default DocumentsColumn;
